'use client'

import { useEffect } from 'react'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import './cookieconsent.css'
import { cookieConsentConfig } from './config'

/*
 * The site's initial PageLoader sets `html[data-loader-pending]` which applies
 * `body { visibility: hidden }` via globals.css. If we run CookieConsent while
 * the loader is still active, its entrance animation plays against a hidden
 * DOM and the banner pops in abruptly when the loader exits.
 *
 * We wait for the loader to complete (signal: `body.page-loaded` class, which
 * the PageLoader adds on its completion timer) before initialising. A timeout
 * fallback covers the case where the loader never fires (e.g. disabled
 * animation, an error, or a future change).
 */
const LOADER_TIMEOUT_MS = 3000

function loaderIsComplete(): boolean {
  return (
    document.body.classList.contains('page-loaded') ||
    document.documentElement.hasAttribute('data-loader-complete')
  )
}

export function CookieConsentProvider() {
  useEffect(() => {
    let cancelled = false
    let started = false

    const start = () => {
      if (cancelled || started) return
      started = true
      void import('vanilla-cookieconsent').then((mod) => {
        if (cancelled) return
        mod.run(cookieConsentConfig)
        // Expose the module API on window so non-module consumers (e.g. the
        // footer "Cookie-Einstellungen" button) can call showPreferences()
        // without re-importing the library.
        ;(window as unknown as { CookieConsent: typeof mod }).CookieConsent = mod
      })
    }

    if (loaderIsComplete()) {
      start()
      return () => {
        cancelled = true
      }
    }

    const observer = new MutationObserver(() => {
      if (loaderIsComplete()) start()
    })
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-loader-complete'],
    })

    const fallback = window.setTimeout(start, LOADER_TIMEOUT_MS)

    return () => {
      cancelled = true
      observer.disconnect()
      window.clearTimeout(fallback)
    }
  }, [])

  return null
}
